import { FC, useEffect, useState } from 'react';
import {
  NudosButton,
  NudosMaskedNumberInput,
  NudosTextInput,
  NudosUploadDownloadButton
} from '../../../../../../../../components/NudosComponents';
import { IlogisticServiceDetails, IlogisticServiceTrelloCardData } from '../../../../../../../../types/services';
import {
  BILLING_METHODS_NAME,
  listOfPaymentMethodsAndExchangeRates,
  PAYMENT_TYPES,
  PAYMENTS_PROVIDER,
  TYPES_OF_BILLING
} from '../../../../../../../../types/orders';
import { putLogisticServicePaymentLink, updateNewCreditDate } from '../../../../../../../../services/services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../../utils/displayNudosStandardNotifications';
import { uploadLogisticServiceFile } from '../../../../../../utils/logisticServices.utils';
import { IconSucessGreen } from '../../../../../../../../assets/images/services';
import { LogisticServiceLeaveChargeAtZero } from '../../../LogisticServiceLeaveChargeAtZero';
import { PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT } from '../../../../../../../../constants/constants';
import { InformationIcon } from '../../../../../../../../assets/images/shopping';
import PaymentDetailsModal from '../../../../../../../../components/PaymentDetailsModal/PaymentDetailsModal';
import LogisticServicePaymentInformation from '../../../../../../../services/components/LogisticsServiceTrelloCard/Components/LogisticServicePaymentInformation/LogisticServicePaymentInformation';
import ModalAddPaymentLink from '../../../../../../../services/components/ModalsLogisticsService/ModalAddPaymentLink/ModalAddPaymentLink';
import NudosDatePicker from '../../../../../../../../components/NudosComponents/NudosDatePicker';
import CreditDaysCounter from '../../../../../../../../components/CreditDaysCounter/CreditDaysCounter';

import './PaymentMethodComponent.scss';

const PaymentMethodComponent: FC<IPaymentMethodComponent> = ({
  logisticServiceData,
  updateWithLoaders,
  updateWithoutLoader
}: IPaymentMethodComponent) => {
  const [stripePaymentLink, setStripePaymentLink] = useState<string>('');
  const [editStripeLink, setEditStripeLink] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [loadingProofOfPayment, setLoadingProofOfPayment] = useState(false);
  const [showCarePayamentDetails, setShowCarePaymentDetails] = useState<boolean>(false);
  const [showBankAccountInfoModal, setShowBankAccountInfoModal] = useState<boolean>(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState<boolean>(false);
  const [creditDate, setCreditDate] = useState<string>();
  const [isEditCreditDate, setIsEditCreditDate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    paymentLink,
    id,
    proofOfPaymentUrl,
    billingCountry,
    manualQuote,
    paymentMethod,
    paymentType,
    billingMethod,
    isPaymentZero,
    hasActiveCredit,
    purchaseOrder,
    paymentAgreementDate,
    bankAccountInfo,
    quoteDocumentUrl,
    quotesLog,
    statusName,
    isPaymentAgreement,
    isPaymentReceived
  } = logisticServiceData;

  const { billingMethodName, billingProvider } = billingMethod || {};

  const thePaymentWasMadeByCard = paymentMethod?.toLowerCase()?.includes('tarjeta');
  const thePaymentWasMadeBordCredit = !!paymentMethod?.toLowerCase()?.includes('crédito bord');
  const daysOfThePaymentAgreement = Number(logisticServiceData?.paymentAgreementDays || 0);
  const showChargeAtZeroComponente = !!logisticServiceData?.id;

  const wasPaidThroughStripe = proofOfPaymentUrl === PAYMENTS_PROVIDER;
  const proofOfPaymentLink = wasPaidThroughStripe ? paymentLink : proofOfPaymentUrl;
  const isNudosCare = paymentType === 'Care';
  const careDoesNotCoverTheEntirePayment = true;
  const careShowDoesNotCoverTheEntirePayment = isNudosCare && careDoesNotCoverTheEntirePayment;

  //Logic show elements
  const showLinkAndProofAutomatedPayment =
    paymentMethod && PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT.includes(paymentMethod);

  const showComponentToLoadLink = showLinkAndProofAutomatedPayment;
  //The transfer in Mexico has exceptions
  const transferInMexico = paymentMethod === PAYMENT_TYPES.TRANSFER_MXN;
  const currencyIsNotUSD = logisticServiceData?.currency && logisticServiceData?.currency !== 'USD';

  const transferInCLP = paymentMethod === PAYMENT_TYPES.TRANSFER_CLP;
  const transferInBRL = paymentMethod === PAYMENT_TYPES.TRANSFER_BRL;

  const isStripeUSAPayment =
    billingProvider &&
    [TYPES_OF_BILLING?.STRIPE_USA_NUDOS_CORP, TYPES_OF_BILLING?.STRIPE_USA_NUDOS_LLC].includes(billingProvider);

  const showBlueTextStripe =
    (showLinkAndProofAutomatedPayment || transferInMexico || isStripeUSAPayment) &&
    !proofOfPaymentLink &&
    !isPaymentZero &&
    !hasActiveCredit;

  const paymentAutomatizated =
    billingProvider &&
    !purchaseOrder &&
    !paymentAgreementDate &&
    [
      TYPES_OF_BILLING?.GIGSTACK,
      TYPES_OF_BILLING?.STRIPE_USA_NUDOS_LLC,
      TYPES_OF_BILLING?.STRIPE_USA_NUDOS_CORP
    ].includes(billingProvider);

  const isManualLogistic = !paymentAutomatizated || hasActiveCredit;
  const manualOrAutomatizatedLogistic = isManualLogistic ? '- Manual' : '- Automatizado';
  const displayViewPaymentMethodsModal =
    paymentMethod?.includes('Transf') && bankAccountInfo && !!Object.keys(bankAccountInfo).length;

  const paymentData = {
    paymentLink,
    paymentMethod,
    paymentType,
    paymentAgreementDate,
    isPaymentAgreement,
    isPaymentReceived,
    creditDate
  };

  const defaultValues = () => {
    setStripePaymentLink(paymentLink || '');
    setCreditDate(paymentAgreementDate || '');
  };

  const handleStripeLink = (linkValue: string) => {
    setEditStripeLink(true);
    setStripePaymentLink(linkValue);
  };

  const updatePaymentLink = async () => {
    if (buttonLoading || !id || !stripePaymentLink) return;
    setButtonLoading(true);
    try {
      await putLogisticServicePaymentLink(id, { paymentLink: stripePaymentLink });
      displaySuccessNotification({ customJSXMessage: <>Se cargó el link de pago exitosamente</> });
      updateWithLoaders && updateWithLoaders();
    } catch {
      displayErrorNotification();
    } finally {
      setButtonLoading(false);
    }
  };

  const componentLabelAccordingToPaymentMethod = (paymentMethod: PAYMENT_TYPES, hasActiveCredit?: boolean) => {
    const transfMX = PAYMENT_TYPES.TRANSFER_MXN === paymentMethod;
    const textSeeReceipt =
      !transfMX &&
      [
        PAYMENT_TYPES.TRANSFER_USD,
        PAYMENT_TYPES.TRANSFER_COP,
        PAYMENT_TYPES.TRANSFER_ARS,
        PAYMENT_TYPES.TRANSFER_BRL
      ].includes(paymentMethod);
    if (hasActiveCredit) return 'Ver comprobante';
    if (transfMX) return 'Pago verificado por Stripe';
    if (textSeeReceipt) return 'Ver Comprobante';
    return 'Pago realizado por Stripe';
  };

  const uploadProofOfPayment = (proofOfPaymentFile?: File) => {
    uploadLogisticServiceFile({
      logisticServiceId: id,
      type: 'proofOfPayment',
      file: proofOfPaymentFile,
      handleLoading: setLoadingProofOfPayment,
      updateDataCallback: updateWithoutLoader,
      billingCountryId: Number(billingCountry?.id)
    });
  };

  const requestCarePayment = (state: boolean) => setShowCarePaymentDetails(state);

  const findBillingMethods = () => {
    if (billingCountry?.code === 'uy' && billingMethod) {
      switch (billingMethodName) {
        case BILLING_METHODS_NAME.LOCAL_SALE_URUGUAY:
        case BILLING_METHODS_NAME.LOCAL_SALE_FREE_ZONE_ENTITY_URUGUAY:
          return 'local';
        case BILLING_METHODS_NAME.INTERNATIONAL_SALE_URUGUAY:
          return 'internacional';
        default:
          return billingMethodName;
      }
    }
    return '';
  };

  const getPaymentMethodDetails = (paymentMethod?: PAYMENT_TYPES) => {
    if (!paymentMethod) return '';
    const thereIsAnExchangeRate = !!logisticServiceData?.exchangeRate && !!logisticServiceData?.currency;
    const validPaymentMethodToDisplayExchangeRate = listOfPaymentMethodsAndExchangeRates.includes(paymentMethod);
    if (!thereIsAnExchangeRate || !validPaymentMethodToDisplayExchangeRate) return '';
    return (
      <span className="font-normal">{`(tasa de cambio: ${logisticServiceData?.exchangeRate} ${logisticServiceData?.currency})`}</span>
    );
  };

  const getTypeOfBilling = (paymentMethod?: PAYMENT_TYPES, typeOfBilling?: TYPES_OF_BILLING) => {
    if (typeOfBilling === TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC) return '(LLC)';
    if (typeOfBilling === TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP) return '(C-Corp)';
    switch (paymentMethod) {
      case PAYMENT_TYPES.TRANSFER_USD:
      case PAYMENT_TYPES.INTERNATIONAL_CARD:
        return `(${billingCountry?.name}  ${findBillingMethods()})`;
      default:
        return '';
    }
  };

  const paymentMethodDetails = getPaymentMethodDetails(paymentMethod);
  const typeOfBillingDetails = getTypeOfBilling(paymentMethod, billingProvider);

  const saveNewCreditDate = async () => {
    if (!id || !creditDate) return;
    setIsLoading(true);
    try {
      await updateNewCreditDate(Number(id), creditDate || '');
      await updateWithoutLoader?.();
      setIsEditCreditDate(false);
    } catch {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    defaultValues();
  }, [logisticServiceData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id="paymentMethodComponent">
      {showBankAccountInfoModal && (
        <PaymentDetailsModal
          paymentDetailsModalData={logisticServiceData}
          closeModalCallback={() => setShowBankAccountInfoModal(false)}
        />
      )}
      {showPaymentLinkModal && (
        <ModalAddPaymentLink
          closeModalCallback={() => setShowPaymentLinkModal(false)}
          logisticServiceData={logisticServiceData as IlogisticServiceTrelloCardData}
          updateTrelloDataCallback={updateWithLoaders}
        />
      )}
      <div className="methodTitle">Forma de pago</div>
      <LogisticServicePaymentInformation
        id={id}
        paymentData={paymentData}
        status={statusName}
        quoteDocumentUrl={quoteDocumentUrl}
        openPaymentLinkModalCallback={() => setShowPaymentLinkModal(true)}
        proofOfPaymentUrl={proofOfPaymentUrl}
        manualQuote={manualQuote}
        quotesLog={quotesLog}
        isLogisticDetailsView
      />
      <div className="methodItemsContainer">
        {isNudosCare && (
          <div className="careContainer">
            <div className="checkIconContainer">
              <IconSucessGreen />
            </div>
            <div className="textCareContainer">Cubierto con Nudos Care</div>
          </div>
        )}
        {(!isNudosCare || (isNudosCare && showCarePayamentDetails)) && (
          <>
            <div className="methodName">
              {paymentMethod} {typeOfBillingDetails}
              <div className="manualOrAutomatizatedLogistic"> {manualOrAutomatizatedLogistic}</div>
              {paymentMethodDetails}
              {displayViewPaymentMethodsModal && (
                <div className="viewBankAccountInfo" onClick={() => setShowBankAccountInfoModal(true)}>
                  <InformationIcon className="informationIcon" />
                  Ver datos de pago
                </div>
              )}
            </div>
            {showComponentToLoadLink && (
              <div className="paymentLinkContainer">
                <div className="inputLinkContainer">
                  <NudosTextInput
                    isFilled={!!stripePaymentLink}
                    label="Link de pago"
                    placeholder="Ingresa el link de stripe"
                    defaultValue={stripePaymentLink}
                    handleChange={e => handleStripeLink(e)}
                    isDeactivated={!!proofOfPaymentLink || !!isPaymentZero}
                  />
                </div>
                <div className="buttonLinkContainer">
                  <NudosButton
                    buttonText="Guardar"
                    customWidth="69px"
                    customHeight="28px"
                    isButtonLoading={buttonLoading}
                    handleClick={updatePaymentLink}
                    isButtonDisabled={!editStripeLink || !stripePaymentLink || buttonLoading || !!isPaymentZero}
                  />
                </div>
              </div>
            )}

            {!transferInCLP && !transferInBRL && (
              <div className="mb-6">
                <div className="flex items-center gap-6">
                  {currencyIsNotUSD && logisticServiceData?.total && (
                    <NudosMaskedNumberInput
                      label={`Total a pagar en ${logisticServiceData?.currency}`}
                      currentValue={logisticServiceData?.total as string}
                      numberOfDecimals={2}
                      maskPrefix="$"
                      maskSufix={logisticServiceData?.currency as string}
                      customClassName="totalPay onlyTotalPay"
                      isDeactivated
                    />
                  )}
                  {logisticServiceData?.totalUsd && (
                    <NudosMaskedNumberInput
                      label="Total a pagar en USD"
                      currentValue={logisticServiceData?.totalUsd as string}
                      numberOfDecimals={2}
                      maskPrefix="$"
                      maskSufix="USD"
                      isDeactivated
                      customClassName="totalPay onlyTotalPay"
                    />
                  )}
                </div>
                {(!paymentAutomatizated || hasActiveCredit) && (
                  <span className="toEditAmountText">Para editar el monto, debes cargar una nueva cotización</span>
                )}
              </div>
            )}

            <div className="proofOfPaymentContainer">
              <div className="proofOfPaymentFileContainer">
                {!!isPaymentZero ? (
                  <NudosUploadDownloadButton
                    customClassName="logisticServiceDocumentsUploadButton"
                    customWidth="280px"
                    customHeight="38px"
                    uploadedFileHandler={e => {}}
                    buttonEmptyText="Comprobante"
                    downloadButtonText="Ver comprobante"
                    isDisabled={true}
                  />
                ) : (
                  <NudosUploadDownloadButton
                    customClassName="logisticServiceDocumentsUploadButton"
                    customWidth="280px"
                    customHeight="38px"
                    uploadedFileHandler={e => uploadProofOfPayment(e)}
                    buttonEmptyText="Comprobante"
                    buttonFilledText="Comprobante"
                    downloadButtonText={
                      proofOfPaymentLink && paymentMethod
                        ? componentLabelAccordingToPaymentMethod(paymentMethod, hasActiveCredit)
                        : 'Ver comprobante'
                    }
                    currentFileUrl={proofOfPaymentLink || ''}
                    isDisabled={loadingProofOfPayment || !!isPaymentZero}
                    showSkeletonLoader={loadingProofOfPayment}
                  />
                )}
              </div>
              {showBlueTextStripe && (
                <div className="paymentNoticeTextContainer">Pago pendiente de verificar por Stripe</div>
              )}
            </div>
          </>
        )}
        {!thePaymentWasMadeByCard && (
          <div className="agreementDateContainer">
            <div className="datePickerAndButtonContainer">
              <NudosDatePicker
                customClassName="customDatePicker"
                updateCurrentValueCallback={e => {
                  setCreditDate(e);
                  setIsEditCreditDate(true);
                }}
                defaultValue={creditDate}
                label="Fecha de compromiso de pago"
                isFilled={!!creditDate}
                desiredTimeZone={undefined} // MX by default, since its information used by operations
              />
              <NudosButton
                customClassName="saveButtonAgreementDate"
                buttonText="Guardar"
                isButtonLoading={isLoading}
                isButtonDisabled={!creditDate || !isEditCreditDate}
                handleClick={saveNewCreditDate}
              />
            </div>

            <CreditDaysCounter
              customClassCreditDaysCounter="customClassCreditDaysCounter"
              days={daysOfThePaymentAgreement}
              showCreditDaysCounter={thePaymentWasMadeBordCredit}
            />
          </div>
        )}
      </div>

      {!isNudosCare && manualQuote && (
        <LogisticServiceLeaveChargeAtZero
          isPaymentZero={!!isPaymentZero}
          logisticsServiceId={Number(logisticServiceData.id)}
          showComponent={showChargeAtZeroComponente}
          updateWithLoaders={updateWithLoaders}
          careShowDoesNotCoverTheEntirePayment={careShowDoesNotCoverTheEntirePayment}
          listenToExternalChange={requestCarePayment}
        />
      )}
    </div>
  );
};

export default PaymentMethodComponent;

interface IPaymentMethodComponent {
  logisticServiceData: IlogisticServiceDetails;
  updateWithLoaders: () => Promise<void>;
  updateWithoutLoader: () => Promise<void>;
}
